import {
  pgTable,
  uuid,
  varchar,
  bigint,
  text,
  index,
  foreignKey,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { identity_user } from '@trashlab/api/src/domains/identity/schemas/identity-user';
import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';
import { isoTimestamp } from '@trashlab/api/src/domains/core/schemas/base-template';

/**
 * Tracks the processing state of GPS pings for each driver.
 * Used to implement watermarking and avoid reprocessing data from Redis streams.
 */
export const gps_processing_state = pgTable(
  'gps_processing_state',
  {
    ...defaultFields.fieldsWithTenantId(),
    /** The identity user (driver) this state belongs to */
    identityUserId: uuid('identityUserId').notNull().unique(),
    /** Timestamp of the last processed GPS ping */
    lastProcessedTimestamp: isoTimestamp('lastProcessedTimestamp', {
      withTimezone: true,
    }),
    /** Redis stream ID of the last processed message */
    lastProcessedRedisStreamId: varchar('lastProcessedRedisStreamId', { length: 255 }),
    /** ID of the last processed GPS ping record */
    lastProcessedGpsPingId: uuid('lastProcessedGpsPingId'),
    /** Total number of GPS pings processed for this driver */
    processedCount: bigint('processedCount', { mode: 'number' }).default(0).notNull(),
    /** Timestamp of the last processing error */
    lastErrorAt: isoTimestamp('lastErrorAt', {
      withTimezone: true,
    }),
    /** Last error message if any */
    lastErrorMessage: text('lastErrorMessage'),
  },
  (table) => ({
    // Standard indexes
    createdAtTs_idx: index('gps_processing_state_createdAtTs_idx').on(table.createdAtTs),
    tenantId_idx: index('gps_processing_state_tenantId_idx').on(table.tenantId),
    updatedAtTs_idx: index('gps_processing_state_updatedAtTs_idx').on(table.updatedAtTs),
    updatedAtTs_idx1: index('gps_processing_state_updatedAtTs_idx1').on(
      table.updatedAtTs.desc(),
    ),
    versionNumber_idx: index('gps_processing_state_versionNumber_idx').on(
      table.versionNumber,
    ),
    
    // Table-specific indexes
    identityUserId_idx: index('gps_processing_state_identityUserId_idx').on(
      table.identityUserId,
    ),
    
    // Composite index for efficient lookups by tenantId and identityUserId
    tenant_identity_idx: index('idx_gps_processing_state_tenant_identity').on(
      table.tenantId,
      table.identityUserId
    ),
    
    // Foreign key to identity_user
    gps_processing_state_identityUserId_fkey: foreignKey({
      columns: [table.identityUserId],
      foreignColumns: [identity_user.id],
      name: 'gps_processing_state_identityUserId_fkey',
    }),
  })
);

/**
 * History table for GPS processing state
 */
export const gps_processing_state_history = pgTable('gps_processing_state_history', {
  ...defaultHistoryFields.historyFieldsWithTenantId(),
  identityUserId: uuid('identityUserId'),
  lastProcessedTimestamp: isoTimestamp('lastProcessedTimestamp', {
    withTimezone: true,
  }),
  lastProcessedRedisStreamId: varchar('lastProcessedRedisStreamId', { length: 255 }),
  lastProcessedGpsPingId: uuid('lastProcessedGpsPingId'),
  processedCount: bigint('processedCount', { mode: 'number' }),
  lastErrorAt: isoTimestamp('lastErrorAt', {
    withTimezone: true,
  }),
  lastErrorMessage: text('lastErrorMessage'),
});

/** Type inference for GPS processing state */
export type GpsProcessingState = typeof gps_processing_state.$inferSelect;
export type NewGpsProcessingState = typeof gps_processing_state.$inferInsert; 